import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reputation' | 'contact'>('intro');

  const tabs = [
    { key: 'intro' as const, label: '品牌介绍', icon: 'ri-building-2-line' },
    { key: 'reputation' as const, label: '口碑声誉', icon: 'ri-star-line' },
    { key: 'contact' as const, label: '联系我们', icon: 'ri-mail-line' },
  ];

  const schemaJson = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于韩国漫画网',
      description: '韩国漫画网 - 领先的韩漫在线阅读平台，提供海量正版韩国漫画资源。了解我们的品牌故事与核心优势。',
      url: 'https://www.manhuawang.cn/about',
      isPartOf: {
        '@type': 'WebSite',
        name: '韩国漫画网',
        url: 'https://www.manhuawang.cn',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.manhuawang.cn' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.manhuawang.cn/about' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '韩国漫画网是什么平台？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '韩国漫画网是国内领先的韩国漫画在线阅读平台，提供海量正版韩国漫画资源，涵盖热血、恋爱、校园等多种类型，每日与韩国同步更新。',
          },
        },
        {
          '@type': 'Question',
          name: '韩国漫画网的漫画需要付费吗？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '韩国漫画网所有漫画资源均免费提供在线阅读，无需任何付费订阅，致力于为广大漫迷提供优质的免费阅读体验。',
          },
        },
        {
          '@type': 'Question',
          name: '如何联系韩国漫画网？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '您可以通过网站的联系我们页面提交表单，或发送邮件至 support@manhuawang.cn，我们的客服团队将在24小时内回复。',
          },
        },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 - 韩国漫画网 - 领先的韩漫在线阅读平台"
        description="韩国漫画网关于我们页面，了解品牌故事、平台优势与口碑声誉。国内领先的韩漫在线阅读平台，海量正版韩漫资源免费畅读。"
        keywords="韩国漫画网,关于我们,韩漫平台,品牌介绍,联系我们,韩漫在线阅读"
        canonicalUrl="https://www.manhuawang.cn/about"
        ogTitle="关于韩国漫画网 - 领先的韩漫在线阅读平台"
        ogDescription="了解韩国漫画网的品牌故事与核心优势。海量正版韩漫资源免费在线阅读。"
        schemaJson={schemaJson}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2 font-heading">
              关于我们
            </h1>
            <p className="text-sm text-foreground-500 mb-8">
              了解韩国漫画网的品牌故事与发展历程
            </p>

            <div className="flex gap-1 mb-8 bg-background-100 rounded-full px-1 py-1 w-fit">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-full transition-all whitespace-nowrap cursor-pointer ${
                    activeTab === tab.key
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={tab.icon}></i>
                  </div>
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'intro' && (
              <section className="pb-14">
                <div className="space-y-8">
                  <div className="relative rounded-xl overflow-hidden h-[280px] md:h-[360px]">
                    <img
                      src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20style%20illustration%20of%20modern%20creative%20office%20or%20studio%20space%2C%20warm%20coral%20and%20cream%20tones%2C%20bookshelves%20with%20comics%2C%20cozy%20and%20inviting%20atmosphere%2C%20professional%20digital%20art%2C%20clean%20composition&width=1200&height=500&seq=about-intro-bg&orientation=landscape"
                      alt="韩国漫画网品牌形象"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                      <h2 className="text-xl md:text-2xl font-bold text-background-50 font-heading">
                        韩国漫画网 — 你的韩漫乐园
                      </h2>
                      <p className="text-sm text-background-200/90 mt-2 max-w-xl">
                        自2023年创立以来，韩国漫画网致力于为广大中文读者提供最优质、最便捷的韩漫在线阅读服务。
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-6 rounded-xl bg-background-100">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                        <i className="ri-eye-line text-xl"></i>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">我们的愿景</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        让每一位中文读者都能无障碍地享受到优质韩国漫画的魅力，成为连接中韩漫画文化的桥梁。
                      </p>
                    </div>
                    <div className="p-6 rounded-xl bg-background-100">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                        <i className="ri-rocket-line text-xl"></i>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">我们的使命</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        提供海量、正版、免费的韩国漫画资源，以最快的更新速度、最优质的阅读体验服务广大漫迷。
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { num: '2000+', label: '漫画作品', icon: 'ri-bookmark-line' },
                      { num: '300万+', label: '活跃读者', icon: 'ri-user-heart-line' },
                      { num: '8', label: '漫画分类', icon: 'ri-grid-line' },
                      { num: '99.9%', label: '服务可用率', icon: 'ri-shield-check-line' },
                    ].map((stat) => (
                      <div key={stat.label} className="p-5 rounded-xl bg-background-100 text-center">
                        <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                          <i className={`${stat.icon} text-lg`}></i>
                        </div>
                        <p className="text-xl font-bold text-primary-500">{stat.num}</p>
                        <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                      </div>
                    ))}
                  </div>

                  <div className="p-6 rounded-xl bg-background-100">
                    <h3 className="text-base font-semibold text-foreground-900 mb-4">核心优势</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mt-0.5">
                          <i className="ri-flashlight-line"></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-foreground-800">极速更新</h4>
                          <p className="text-xs text-foreground-500 mt-1 leading-relaxed">与韩国同步更新最新章节，部分热门作品实现实时追更。</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mt-0.5">
                          <i className="ri-shield-check-line"></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-foreground-800">无广告纯净阅读</h4>
                          <p className="text-xs text-foreground-500 mt-1 leading-relaxed">全程无广告干扰，沉浸式阅读体验，让读者专注于漫画本身。</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mt-0.5">
                          <i className="ri-smartphone-line"></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-foreground-800">全平台适配</h4>
                          <p className="text-xs text-foreground-500 mt-1 leading-relaxed">支持PC、平板、手机全平台阅读，随时随地畅享韩漫精彩。</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {activeTab === 'reputation' && (
              <section className="pb-14">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                  {[
                    { platform: '漫画爱好者社区', rating: 4.9, reviews: 12500, comment: '更新超快，画质很棒，我的韩漫首选平台！' },
                    { platform: '读者反馈平台', rating: 4.8, reviews: 8900, comment: '分类清晰，搜索方便，无广告体验太赞了。' },
                    { platform: '二次元资讯站', rating: 4.9, reviews: 15600, comment: '海量正版资源，翻译质量一流，推荐给所有韩漫爱好者。' },
                  ].map((item) => (
                    <div key={item.platform} className="p-6 rounded-xl bg-background-100">
                      <h3 className="text-sm font-semibold text-foreground-800 mb-3">{item.platform}</h3>
                      <div className="flex items-center gap-1 mb-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className="w-4 h-4 flex items-center justify-center">
                            <i className={`text-sm ${i < Math.floor(item.rating) ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                          </div>
                        ))}
                        <span className="ml-2 text-lg font-bold text-foreground-900">{item.rating}</span>
                      </div>
                      <p className="text-xs text-foreground-500 mb-3">{item.reviews.toLocaleString()} 条评价</p>
                      <p className="text-sm text-foreground-600 italic leading-relaxed">"{item.comment}"</p>
                    </div>
                  ))}
                </div>

                <div className="p-6 rounded-xl bg-background-100">
                  <h3 className="text-base font-semibold text-foreground-900 mb-4">媒体关注</h3>
                  <div className="space-y-4">
                    {[
                      { title: '韩国漫画网获评年度最佳漫画阅读平台', date: '2026-07-15', source: '动漫资讯网' },
                      { title: '中韩漫画文化交流论坛在韩国漫画网总部举行', date: '2026-06-20', source: '文化传播周刊' },
                      { title: '韩国漫画网用户突破300万，韩漫阅读市场持续增长', date: '2026-05-10', source: '数字出版观察' },
                      { title: '韩国漫画网推出AI智能推荐功能，提升阅读体验', date: '2026-04-02', source: '科技前沿日报' },
                    ].map((news) => (
                      <div key={news.title} className="flex items-start gap-4 pb-4 border-b border-background-200/70 last:border-0 last:pb-0">
                        <div className="w-2 h-2 mt-1.5 rounded-full bg-primary-400 flex-shrink-0"></div>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-foreground-800">{news.title}</h4>
                          <p className="text-xs text-foreground-400 mt-1">{news.date} · {news.source}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {activeTab === 'contact' && (
              <section className="pb-14">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground-900 mb-6 font-heading">联系方式</h2>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                          <i className="ri-mail-line text-lg"></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-foreground-800">电子邮箱</h4>
                          <p className="text-sm text-foreground-500">support@manhuawang.cn</p>
                          <p className="text-xs text-foreground-400 mt-0.5">客服将在 24 小时内回复</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                          <i className="ri-phone-line text-lg"></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-foreground-800">客服热线</h4>
                          <p className="text-sm text-foreground-500">400-888-9999</p>
                          <p className="text-xs text-foreground-400 mt-0.5">工作日 9:00 - 18:00</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                          <i className="ri-map-pin-line text-lg"></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-foreground-800">公司地址</h4>
                          <p className="text-sm text-foreground-500">韩国首尔特别市江南区</p>
                          <p className="text-xs text-foreground-400 mt-0.5">서울특별시 강남구</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 rounded-xl bg-background-100">
                    <h2 className="text-lg font-semibold text-foreground-900 mb-4 font-heading">发送消息</h2>
                    <ContactForm />
                  </div>
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}

function ContactForm() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('idle');
    setFormError('');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9pdplqftmtnfcfh0l9g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: unknown;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed && typeof parsed === 'object' && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        const msg = (parsed && typeof parsed === 'object')
          ? ((parsed as Record<string, unknown>).meta as Record<string, unknown>)?.message as string
            || (parsed as Record<string, unknown>).message as string
          : responseText || '提交失败，请稍后重试';
        if (typeof msg === 'string' && msg.includes('spam')) {
          setFormStatus('success');
        } else {
          setFormStatus('error');
          setFormError(typeof msg === 'string' ? msg : '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络异常，请稍后重试');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form>
      <div>
        <input
          type="text"
          name="name"
          placeholder="您的姓名"
          required
          className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-all"
        />
      </div>
      <div>
        <input
          type="email"
          name="email"
          placeholder="您的邮箱"
          required
          className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-all"
        />
      </div>
      <div>
        <textarea
          name="message"
          placeholder="请输入您的留言..."
          rows={4}
          maxLength={500}
          required
          onChange={(e) => setCharCount(e.target.value.length)}
          className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-all resize-none"
        ></textarea>
        <p className="text-xs text-foreground-400 mt-1 text-right">{charCount}/500</p>
      </div>
      <input
        type="text"
        name="phone_alt"
        className="honeypot-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />
      <button
        type="submit"
        className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
      >
        发送消息
      </button>
      {formStatus === 'success' && (
        <p className="text-sm text-green-600 text-center">消息已成功发送，我们会尽快回复您！</p>
      )}
      {formStatus === 'error' && (
        <p className="text-sm text-red-500 text-center">{formError || '提交失败，请稍后重试'}</p>
      )}
    </form>
  );
}